import * as THREE from 'three';

/** Shader define that enables 3D-field sampling. Absent ⇒ compiled path matches today. */
export const DAMAGE_FIELD_DEFINE = 'USE_DAMAGE_FIELD';

/**
 * Shared uniforms. Every surface family points at these objects so a bind
 * or time tick updates every compiled program without a re-upload.
 */
export const damageFieldUniforms = {
  uDamageField: { value: null as THREE.Data3DTexture | null },
  uDamageFieldHalfExtents: { value: new THREE.Vector3(1, 1, 1) },
  uDamageFieldTime: { value: 0 },
};

const consumers = new Set<THREE.Material>();
const disposeOriginals = new WeakMap<THREE.Material, () => void>();
let bound = false;

export function isDamageFieldBound(): boolean {
  return bound && damageFieldUniforms.uDamageField.value !== null;
}

export function damageFieldProgramParts(): Array<string | number | boolean> {
  return isDamageFieldBound() ? ['df'] : [];
}

function applyDamageFieldDefines(material: THREE.Material): void {
  const mat = material as THREE.MeshStandardMaterial;
  if (!mat.defines) mat.defines = {};
  if (isDamageFieldBound()) {
    mat.defines[DAMAGE_FIELD_DEFINE] = '';
  } else if (DAMAGE_FIELD_DEFINE in mat.defines) {
    delete mat.defines[DAMAGE_FIELD_DEFINE];
  }
}

/**
 * Track a surface material so a later bind/unbind can flip `USE_DAMAGE_FIELD`
 * and force a program rebuild. `dispose()` unregisters automatically.
 */
export function registerDamageFieldConsumer(material: THREE.Material): void {
  if (consumers.has(material)) {
    applyDamageFieldDefines(material);
    return;
  }
  consumers.add(material);
  applyDamageFieldDefines(material);
  if (!disposeOriginals.has(material)) {
    const previous = material.dispose.bind(material);
    disposeOriginals.set(material, previous);
    material.dispose = () => {
      consumers.delete(material);
      disposeOriginals.delete(material);
      previous();
    };
  }
}

/** Bind (or clear) the rest-space RGBA8 volume. Null unbinds and drops the define. */
export function bindDamageFieldTexture(
  texture: THREE.Data3DTexture | null,
  halfExtents?: { x: number; y: number; z: number },
): void {
  damageFieldUniforms.uDamageField.value = texture;
  if (halfExtents) {
    damageFieldUniforms.uDamageFieldHalfExtents.value.set(
      halfExtents.x,
      halfExtents.y,
      halfExtents.z,
    );
  }
  const nextBound = texture !== null;
  if (bound === nextBound) return;
  bound = nextBound;
  for (const material of consumers) {
    applyDamageFieldDefines(material);
    material.needsUpdate = true;
  }
}

export function setDamageFieldTime(normalized: number): void {
  const value = Number.isFinite(normalized) ? Math.min(1, Math.max(0, normalized)) : 0;
  damageFieldUniforms.uDamageFieldTime.value = value;
}

export function assignDamageFieldUniforms(shader: {
  uniforms: Record<string, { value: unknown }>;
}): void {
  Object.assign(shader.uniforms, damageFieldUniforms);
}

/** Test-only: drop module state so files that share a worker stay unbound. */
export function resetDamageFieldBindingForTests(): void {
  damageFieldUniforms.uDamageField.value = null;
  damageFieldUniforms.uDamageFieldHalfExtents.value.set(1, 1, 1);
  damageFieldUniforms.uDamageFieldTime.value = 0;
  bound = false;
  for (const material of consumers) {
    applyDamageFieldDefines(material);
  }
}

/**
 * Vertex-stage uniforms only. The wall is a 2-segment box — a per-vertex
 * `sampler3D` fetch interpolates a tight stamp across whole faces. All
 * visual sampling happens in the fragment stage.
 */
export const DAMAGE_FIELD_COMMON_GLSL = /* glsl */ `
#ifdef USE_DAMAGE_FIELD
uniform sampler3D uDamageField;
uniform vec3 uDamageFieldHalfExtents;
uniform float uDamageFieldTime;
#endif
`;

export const DAMAGE_FIELD_VERTEX_GLSL = /* glsl */ `
#ifdef USE_DAMAGE_FIELD
// Field is sampled per-fragment so stamp locality survives a low-poly wall.
#endif
`;

export const DAMAGE_FIELD_VERTEX_SAMPLE = /* glsl */ `
`;

/**
 * Fragment crack/response helpers. Wrapped in `USE_DAMAGE_FIELD` so an unbound
 * compile strips this block and stays pixel-identical to the pre-binding shaders.
 */
export const DAMAGE_FIELD_GLSL = /* glsl */ `
#ifdef USE_DAMAGE_FIELD
uniform sampler3D uDamageField;
uniform vec3 uDamageFieldHalfExtents;
uniform float uDamageFieldTime;

vec3 sampleDamageField(vec3 restPos) {
  vec3 uvw = restPos / max(2.0 * uDamageFieldHalfExtents, vec3(1e-4)) + 0.5;
  vec4 texel = textureLod(uDamageField, clamp(uvw, 0.0, 1.0), 0.0);
  float damage = texel.r;
  float stampTime = texel.g;
  float seed = texel.b;
  float age = clamp(uDamageFieldTime - stampTime, 0.0, 1.0);
  return vec3(damage, age, seed);
}

// Ignore bilinear bleed below a stored weak-hit floor so a hard disc does
// not bloom into a soft circle. Weak ledger values (~0.02+) still lift.
float damageFieldVisual(float damage) {
  float d = max(damage - 0.014, 0.0);
  return clamp(1.0 - exp(-d * 14.0), 0.0, 1.0);
}

float damageHash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float damageNoise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = damageHash2(i);
  float b = damageHash2(i + vec2(1.0, 0.0));
  float c = damageHash2(i + vec2(0.0, 1.0));
  float d = damageHash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float damageHair(float distM, float halfWidth) {
  float hw = max(halfWidth, 0.00045);
  float fw = max(fwidth(distM), 0.00045);
  return 1.0 - smoothstep(hw, hw + fw * 1.35, distM);
}

// Continuous rest-space hairlines. A walk-to-center on the low-res RGBA8
// volume traced the texel grid as a circuit board — do not reconstruct
// polar coords from the field. Gradient (when present) biases direction
// radially; otherwise a spatial cell hash seeds a stable strike axis.
void damageCrackMask(
  vec3 restPos,
  vec3 restNormal,
  float damage,
  float damageVis,
  out float blotch,
  out float crack,
  out float core
) {
  blotch = 0.0;
  crack = 0.0;
  core = 0.0;

  vec3 an = abs(restNormal);
  vec2 uv = (an.z >= an.x && an.z >= an.y) ? restPos.xy
    : (an.x >= an.y ? restPos.yz : restPos.xz);

  // One stable strike axis per ~impact-sized region. Spatial hash, never
  // time and never the stored seed channel: bilinear interpolation of B is
  // meaningless in stamp-overlap zones, so per-fragment geometry must not
  // hash it. The stored seed is for per-impact tinting/effects where
  // smooth blending is acceptable. Age-seeded axes re-rolled every frame
  // and strobed the cracks.
  float cellSeed = damageHash2(floor(uv / 0.45) + vec2(7.3, 2.6));

  float chew = damageNoise2(uv * 6.2) * 0.48
    + damageNoise2(uv * 14.0 + vec2(2.8, 1.1)) * 0.32
    + damageNoise2(uv * 28.0 + vec2(0.7, 4.2)) * 0.2;
  float az = damageNoise2(uv * 3.4 + vec2(cellSeed * 8.0, 1.7));
  // Patch coverage, not sparse dashes. Noise still breaks the disc silhouette.
  blotch = damageVis * smoothstep(0.30, 0.75, chew) * (0.50 + 0.40 * az);
  // Compact dark core: high stored damage (inner / 35% stamps) + same chew.
  core = damageVis * smoothstep(0.55, 0.84, damage) * smoothstep(0.28, 0.70, chew) * (0.55 + 0.45 * az);

  vec2 grad = vec2(dFdx(damage), dFdy(damage));
  float seed = cellSeed * 6.2831853;
  vec2 dir = vec2(cos(seed), sin(seed));
  float gLen = length(grad);
  if (gLen > 1e-5) dir = normalize(mix(dir, grad / gLen, 0.7));
  vec2 perp = vec2(-dir.y, dir.x);

  vec2 auv = vec2(dot(uv, perp), dot(uv, dir));
  float warp = (damageNoise2(auv * vec2(2.4, 0.9) + 1.7) - 0.5) * 0.38;
  auv.x += warp;
  auv.y += (damageNoise2(auv * vec2(1.1, 2.6) + 4.2) - 0.5) * 0.05;

  // Sparse short strike segments. Weak hits keep 2–3 lines; heavy densifies.
  // Near the core, hairs widen to ~4 mm so they read connected at 1–3 m.
  float spacing = mix(0.15, 0.068, clamp(damageVis, 0.0, 1.0));
  float halfW = mix(0.00055, 0.0020, clamp(core, 0.0, 1.0));
  float lineId = floor(auv.x / spacing + cellSeed * 3.0);
  float saw = abs(fract(auv.x / spacing + cellSeed * 3.0) - 0.5) * spacing;
  float segLen = 0.035 + 0.11 * damageHash2(vec2(lineId, 8.4));
  float along = abs(auv.y - (damageHash2(vec2(lineId, 2.2)) - 0.5) * 0.08);
  float hair = damageHair(saw, halfW) * (1.0 - smoothstep(segLen * 0.55, segLen, along));
  float bSpace = spacing * 1.85;
  float bId = floor((auv.x * 0.7 + auv.y * 0.35) / bSpace + 0.31);
  float bSaw = abs(fract((auv.x * 0.7 + auv.y * 0.35) / bSpace + 0.31) - 0.5) * bSpace;
  float bLen = 0.03 + 0.08 * damageHash2(vec2(bId, 5.1));
  float bAlong = abs(auv.y * 0.7 - auv.x * 0.35);
  float branch = damageHair(bSaw, halfW)
    * (1.0 - smoothstep(bLen * 0.5, bLen, bAlong))
    * smoothstep(0.32, 0.75, damageVis);

  float keep = step(mix(0.72, 0.4, clamp(damageVis, 0.0, 1.0)), damageHash2(vec2(lineId, 11.0)));
  float keepB = step(mix(0.86, 0.58, clamp(damageVis, 0.0, 1.0)), damageHash2(vec2(bId, 4.2)));

  float interior = 0.0;
  if (damage > 0.002) {
    vec3 nrm = normalize(restNormal);
    vec3 t1 = normalize(abs(nrm.y) < 0.92 ? cross(nrm, vec3(0.0, 1.0, 0.0)) : cross(nrm, vec3(1.0, 0.0, 0.0)));
    vec3 t2 = cross(nrm, t1);
    float dMin = damage;
    dMin = min(dMin, sampleDamageField(restPos + t1 * 0.05).x);
    dMin = min(dMin, sampleDamageField(restPos - t1 * 0.05).x);
    dMin = min(dMin, sampleDamageField(restPos + t2 * 0.05).x);
    dMin = min(dMin, sampleDamageField(restPos - t2 * 0.05).x);
    interior = smoothstep(0.03, 0.11, dMin);
  }
  blotch *= interior;
  core *= interior;
#ifdef USE_DAMAGE_DECAL_ATLAS
  // Concrete replaces the analytic lattice with authored atlas stamps.
  crack = 0.0;
#else
  crack = min(1.0, (hair * keep + branch * keepB * 0.7) * 1.6) * interior;
#endif
}

float gDamage = 0.0;
float gDamageVis = 0.0;
float gDamageAge = 0.0;
float gDamageSeed = 0.0;
float gDamageCrack = 0.0;
float gDamageBlotch = 0.0;
float gDamageCore = 0.0;

void fillDamageGlobals(vec3 restPos, vec3 restNormal) {
  vec3 samp = sampleDamageField(restPos);
  gDamage = samp.x;
  gDamageAge = samp.y;
  gDamageSeed = samp.z;
  gDamageVis = damageFieldVisual(gDamage);
  damageCrackMask(restPos, restNormal, gDamage, gDamageVis, gDamageBlotch, gDamageCrack, gDamageCore);
}
#endif
`;
